import { Dispatch, SetStateAction } from 'react'
import {
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

import { AddQuantityTypeButton } from '../add-quantity-type-button.component'
import { QuantityTypesRow } from './quantity-types-row.component'
import { useRemoveQuantityTypes, useTableItemsCheck } from 'src/hooks'

import { IQuantityTypeBaseWithId } from 'src/types'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface IQuantityTypesTableProps {
  quantityTypes?: IQuantityTypeBaseWithId[] | void
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const QuantityTypesTable = ({ quantityTypes, sort, setSort }: IQuantityTypesTableProps) => {
  const {
    handleAllItemsCheck,
    selectedItemsIds,
    setSelectedItemsIds,
    isAllChecked,
    isIndeterminated,
    isAllUnchecked,
    ...itemsCheckProps
  } = useTableItemsCheck({
    items: quantityTypes ?? [],
  })

  const { mutateAsync: removeItems } = useRemoveQuantityTypes()

  const handleItemsRemove = async () => {
    await removeItems(selectedItemsIds)
    setSelectedItemsIds([])
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const theme = useTheme()

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ border: 0 }} sx={{ 'td, th': { border: 0 } }}>
              <TableCell align="center">
                <Checkbox
                  indeterminate={isIndeterminated}
                  sx={{ color: theme.palette.custom.grey2 }}
                  onChange={handleAllItemsCheck}
                  checked={isAllChecked}
                />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('name')}
                  active={sort.split('-')[0] === 'name'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="impact-periods-name-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('category')}
                  active={sort.split('-')[0] === 'category'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-category-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sort.split('-')[0] === 'updated_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <IconButton
                  sx={{ ml: '12px' }}
                  disabled={isAllUnchecked}
                  onClick={handleItemsRemove}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quantityTypes?.map((row) => (
              <QuantityTypesRow {...itemsCheckProps} key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AddQuantityTypeButton />
    </>
  )
}

export { QuantityTypesTable }
